import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Watchlist, WatchlistItem } from '../chart.interface';
import { StorageService } from './storage.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { baseUrl } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class WatchlistService {
  private refreshNeeded$ = new Subject<void>();
  private readonly WATCHLISTS_STORAGE_KEY = 'trading_watchlists';
  private readonly DEFAULT_WATCHLIST_ID = 'default';
  
  private watchlistsSubject = new BehaviorSubject<Watchlist[]>([]);
  private selectedWatchlistSubject = new BehaviorSubject<string | null>(null);
  
  public watchlists$ = this.watchlistsSubject.asObservable();
  public selectedWatchlist$ = this.selectedWatchlistSubject.asObservable();

  constructor(private http: HttpClient, private storagesrv: StorageService) {
    this.loadWatchlists();
  }

  // Load watchlists from localStorage
  private loadWatchlists(): void {
    try {
      const stored = localStorage.getItem(this.WATCHLISTS_STORAGE_KEY);
      let watchlists: Watchlist[] = [];
      
      if (stored && stored.trim().length > 0) {
        try {
          const parsed = JSON.parse(stored);
          
          // Validate parsed data structure
          if (!Array.isArray(parsed)) {
            console.warn('[WARNING] Stored watchlists data is not an array, creating default');
            watchlists = [this.createDefaultWatchlist()];
          } else {
            // Convert date strings back to Date objects with null safety
            watchlists = parsed.map((w: any) => {
              if (!w || typeof w !== 'object') {
                console.warn('[WARNING] Invalid watchlist item found, skipping');
                return null;
              }
              
              return {
                ...w,
                createdDate: w.createdDate ? new Date(w.createdDate) : new Date(),
                lastModified: w.lastModified ? new Date(w.lastModified) : new Date(),
                items: Array.isArray(w.items) ? w.items.map((item: any) => {
                  if (!item || typeof item !== 'object') {
                    console.warn('[WARNING] Invalid watchlist item found, skipping');
                    return null;
                  }
                  return {
                    ...item,
                    addedDate: item.addedDate ? new Date(item.addedDate) : new Date()
                  };
                }).filter((item: any) => item !== null) : []
              };
            }).filter((w: any) => w !== null);
          }
        } catch (parseError) {
          console.error('[ERROR] Failed to parse stored watchlists:', parseError);
          watchlists = [this.createDefaultWatchlist()];
        }
      }
      
      // Create default watchlist if none exist or if parsing failed
      if (watchlists.length === 0) {
        watchlists = [this.createDefaultWatchlist()];
      }
      
      this.watchlistsSubject.next(watchlists);
      
      // Set default selected watchlist with null safety
      const defaultWatchlist = watchlists.find(w => w && w.isDefault) || watchlists[0];
      this.selectedWatchlistSubject.next(defaultWatchlist?.id || null);
      
    } catch (error) {
      console.error('Error loading watchlists:', error);
      // Create default watchlist on error
      const defaultWatchlist = this.createDefaultWatchlist();
      this.watchlistsSubject.next([defaultWatchlist]);
      this.selectedWatchlistSubject.next(defaultWatchlist.id);
    }
  }

  // Save watchlists to localStorage
  private saveWatchlists(): void {
    try {
      const watchlists = this.watchlistsSubject.value;
      localStorage.setItem(this.WATCHLISTS_STORAGE_KEY, JSON.stringify(watchlists));
    } catch (error) {
      console.error('Error saving watchlists:', error);
    }
  }

  // Create default watchlist
  private createDefaultWatchlist(): Watchlist {
    const now = new Date();
    return {
      id: this.DEFAULT_WATCHLIST_ID,
      name: 'My Watchlist',
      description: 'Default watchlist',
      items: [
        // Add some sample tickers
        {
          symbol: 'AAPL',
          displayName: 'Apple Inc.',
          addedDate: now,
          notes: 'Tech giant'
        },
        {
          symbol: 'GOOGL',
          displayName: 'Alphabet Inc.',
          addedDate: now,
          notes: 'Search & AI'
        },
        {
          symbol: 'MSFT',
          displayName: 'Microsoft Corp.',
          addedDate: now,
          notes: 'Cloud & software'
        }
      ],
      createdDate: now,
      lastModified: now,
      isDefault: true
    };
  }

  // Get all watchlists
  getWatchlists(): Watchlist[] {
    return this.watchlistsSubject.value;
  }

  // Get watchlist by ID
  getWatchlist(id: string): Watchlist | null {
    // Null safety check for id
    if (!id || typeof id !== 'string' || id.trim().length === 0) {
      console.warn('[WARNING] Invalid watchlist ID provided');
      return null;
    }

    const watchlists = this.watchlistsSubject.value;
    if (!Array.isArray(watchlists) || watchlists.length === 0) {
      console.warn('[WARNING] No watchlists available');
      return null;
    }

    const found = watchlists.find(w => w && w.id === id);
    return found || null;
  }

  // Get currently selected watchlist
  getSelectedWatchlist(): Watchlist | null {
    const selectedId = this.selectedWatchlistSubject.value;
    if (!selectedId) {
      return null;
    }
    return this.getWatchlist(selectedId);
  }

  // Set selected watchlist
  setSelectedWatchlist(id: string): void {
    // Null safety check for id
    if (!id || typeof id !== 'string' || id.trim().length === 0) {
      console.warn('[WARNING] Invalid watchlist ID provided to setSelectedWatchlist');
      return;
    }

    const watchlist = this.getWatchlist(id);
    if (watchlist) {
      this.selectedWatchlistSubject.next(id);
    } else {
      console.warn(`[WARNING] Watchlist with ID '${id}' not found`);
    }
  }

  // Create new watchlist
  createWatchlist(name: string, description?: string): Watchlist {
    try {
      if (!name || typeof name !== 'string' || name.trim().length === 0) {
        throw new Error('Watchlist name is required');
      }

      const now = new Date();
      const newWatchlist: Watchlist = {
        id: `watchlist_${Date.now()}`,
        name: name.trim(),
        description: description?.trim(),
        items: [],
        createdDate: now,
        lastModified: now,
        isDefault: false
      };

      const watchlists = [...this.watchlistsSubject.value, newWatchlist];
      this.watchlistsSubject.next(watchlists);
      this.saveWatchlists();
      
      return newWatchlist;
    } catch (error) {
      console.error('[ERROR] Error creating watchlist:', error);
      // Return a default watchlist to prevent crashes
      return {
        id: `watchlist_${Date.now()}`,
        name: 'New Watchlist',
        description: 'Default watchlist created due to error',
        items: [],
        createdDate: new Date(),
        lastModified: new Date(),
        isDefault: false
      };
    }
  }

  // Update watchlist
  updateWatchlist(id: string, updates: Partial<Watchlist>): boolean {
    try {
      if (!id || typeof id !== 'string' || id.trim().length === 0) {
        console.warn('[WARNING] Invalid watchlist ID provided to updateWatchlist');
        return false;
      }

      if (!updates || typeof updates !== 'object') {
        console.warn('[WARNING] Invalid updates object provided to updateWatchlist');
        return false;
      }

      const watchlists = this.watchlistsSubject.value;
      const index = watchlists.findIndex(w => w && w.id === id);
      
      if (index === -1) {
        console.warn(`[WARNING] Watchlist with ID '${id}' not found`);
        return false;
      }
      
      const existingWatchlist = watchlists[index];
      if (!existingWatchlist) {
        console.warn(`[WARNING] Invalid watchlist found at index ${index}`);
        return false;
      }

      watchlists[index] = {
        ...existingWatchlist,
        ...updates,
        lastModified: new Date()
      };
      
      this.watchlistsSubject.next([...watchlists]);
      this.saveWatchlists();
      return true;
    } catch (error) {
      console.error('[ERROR] Error updating watchlist:', error);
      return false;
    }
  }

  // Delete watchlist
  deleteWatchlist(id: string): boolean {
    try {
      if (!id || typeof id !== 'string' || id.trim().length === 0) {
        console.warn('[WARNING] Invalid watchlist ID provided to deleteWatchlist');
        return false;
      }

      if (id === this.DEFAULT_WATCHLIST_ID) {
        console.warn('[WARNING] Cannot delete default watchlist');
        return false;
      }
      
      const watchlists = this.watchlistsSubject.value.filter(w => w && w.id !== id);
      this.watchlistsSubject.next(watchlists);
      this.saveWatchlists();
      
      // If deleted watchlist was selected, select default
      if (this.selectedWatchlistSubject.value === id) {
        const defaultWatchlist = watchlists.find(w => w && w.isDefault) || watchlists[0];
        this.selectedWatchlistSubject.next(defaultWatchlist?.id || null);
      }
      
      return true;
    } catch (error) {
      console.error('[ERROR] Error deleting watchlist:', error);
      return false;
    }
  }

  // Add item to watchlist
  addToWatchlist(watchlistId: string, item: Omit<WatchlistItem, 'addedDate'>): boolean {
    const watchlist = this.getWatchlist(watchlistId);
    if (!watchlist) return false;
    
    // Check if item already exists
    const exists = watchlist.items.some(i => i.symbol === item.symbol);
    if (exists) return false;
    
    const newItem: WatchlistItem = {
      ...item,
      addedDate: new Date()
    };
    
    const updatedItems = [...watchlist.items, newItem];
    return this.updateWatchlist(watchlistId, { items: updatedItems });
  }

  // Remove item from watchlist
  removeFromWatchlist(watchlistId: string, symbol: string): boolean {
    const watchlist = this.getWatchlist(watchlistId);
    if (!watchlist) return false;
    
    const updatedItems = watchlist.items.filter(i => i.symbol !== symbol);
    return this.updateWatchlist(watchlistId, { items: updatedItems });
  }

  // Update watchlist item
  updateWatchlistItem(watchlistId: string, symbol: string, updates: Partial<WatchlistItem>): boolean {
    const watchlist = this.getWatchlist(watchlistId);
    if (!watchlist) return false;
    
    const itemIndex = watchlist.items.findIndex(i => i.symbol === symbol);
    if (itemIndex === -1) return false;
    
    const updatedItems = [...watchlist.items];
    updatedItems[itemIndex] = { ...updatedItems[itemIndex], ...updates };
    
    return this.updateWatchlist(watchlistId, { items: updatedItems });
  }

  // Check if symbol is in any watchlist
  isInWatchlist(symbol: string): boolean {
    return this.watchlistsSubject.value.some(w => 
      w.items.some(i => i.symbol === symbol)
    );
  }

  // Get watchlists containing symbol
  getWatchlistsContaining(symbol: string): Watchlist[] {
    return this.watchlistsSubject.value.filter(w => 
      w.items.some(i => i.symbol === symbol)
    );
  }

  // Add symbol to selected watchlist (convenience method)
  addToSelectedWatchlist(symbol: string, displayName?: string, notes?: string): boolean {
    const selectedId = this.selectedWatchlistSubject.value;
    if (!selectedId) return false;
    
    return this.addToWatchlist(selectedId, {
      symbol,
      displayName,
      notes
    });
  }

  // Remove symbol from selected watchlist (convenience method)
  removeFromSelectedWatchlist(symbol: string): boolean {
    const selectedId = this.selectedWatchlistSubject.value;
    if (!selectedId) return false;
    
    return this.removeFromWatchlist(selectedId, symbol);
  }

    getAllWatchList(page: string, disableHttpLoader = 'true') {
      const qp = new URLSearchParams();
      qp.set('page', page);
  
      return this.http.get(`${baseUrl}/tools/dashboard/watchList?` + qp.toString(), {
        headers: new HttpHeaders()
          .set('Authorization', 'Bearer' + ' ' + this.storagesrv.get('token'))
          .set('disableHttpLoader', disableHttpLoader),
      });
    }
  
    removeFromWatchList(_id: any) {
      return this.http.delete(`${baseUrl}/tools/dashboard/watchList/${_id}`, {
        headers: new HttpHeaders().set(
          'Authorization',
          'Bearer' + ' ' + this.storagesrv.get('token')
        ),
      });
    }
  
    removeFromSymbol(symbols: any) {
      const body = { symbol: symbols };
      return this.http.delete(`${baseUrl}/tools/dashboard/watchList/symbol`, {
        body,
        headers: new HttpHeaders().set(
          'Authorization',
          'Bearer' + ' ' + this.storagesrv.get('token')
        ),
      });
    }
  
    getAllSymbolList(_id: any) {
      return this.http.get(`${baseUrl}/tools/dashboard/watchList/symbol/${_id}`, {
        headers: new HttpHeaders().set(
          'Authorization',
          'Bearer' + ' ' + this.storagesrv.get('token')
        ),
      });
    }
  
    createWatchList(formData: any, symbol?: any) {
      // If symbol is provided as second parameter (legacy call), use FormData
      if (symbol !== undefined && typeof formData === 'string') {
        const formDataObj = new FormData();
        formDataObj.append('watchListId', formData);
        formDataObj.append('symbol', symbol);
        
        return this.http.post(`${baseUrl}/tools/dashboard/watchList`, formDataObj, {
          headers: new HttpHeaders().set(
            'Authorization',
            'Bearer' + ' ' + this.storagesrv.get('token')
          ),
        });
      }
      
      // Creating/updating a watchlist with the dynamic structure
      const requestData: any = {
        name: formData.name || '',
        description: formData.description || ''
      };

      if ('symbol' in formData) requestData.symbol = formData.symbol;
      if ('watchListIds' in formData) requestData.watchListIds = formData.watchListIds;
      
      if ('watchListCount' in formData) {
        requestData.watchListCount = formData.watchListCount;
      } else if ('watchListIds' in formData) {
        requestData.watchListCount = formData.watchListIds.length;
      }
      
      return this.http.post(`${baseUrl}/tools/dashboard/watchList`, requestData, {
        headers: new HttpHeaders().set(
          'Authorization',
          'Bearer' + ' ' + this.storagesrv.get('token')
        ),
      });
    }
  
    // Edit watchlist only
    // Local:  baseUrl = 'http://localhost:4444'  -> PATCH http://localhost:4444/tools/dashboard/watchList
    // Stage:  baseUrl = 'https://learning-stage.tradersedgenetwork.com/api'
    //         -> PATCH https://learning-stage.tradersedgenetwork.com/api/tools/dashboard/watchList
    updateWatchList(formData: { watchListId: string; name: string; description?: string }) {
      return this.http.patch(`${baseUrl}/tools/dashboard/watchList`, formData, {
        headers: new HttpHeaders().set(
          'Authorization',
          'Bearer' + ' ' + this.storagesrv.get('token')
        ),
      });
    }
  
    sortSymbolList(data: any, id: any) {
      return this.http.patch(`${baseUrl}/tools/dashboard/watchList/${id}`, data, {
        headers: new HttpHeaders().set(
          'Authorization',
          'Bearer' + ' ' + this.storagesrv.get('token')
        ),
      });
    }
    createDefaultWatchList() {
    return this.http.post(
      `${baseUrl}/tools/dashboard/watchList/default`,
      {},
      {
        headers: new HttpHeaders().set(
          'Authorization',
          `Bearer ${this.storagesrv.get('token')}`
        ),
      }
    );
  }

  // Add symbol to existing watchlist via API
  // POST /tools/dashboard/watchList/ with body:
  // { "name": "", "symbol": "AAPL", "watchListIds": ["<watchlistId>"] }
  addSymbolToWatchList(watchlistId: any, symbol: any) {
    const body: any = {
      name: '',
      symbol: symbol,
      watchListIds: [watchlistId],
      watchListCount: 1
    };

    return this.http.post(`${baseUrl}/tools/dashboard/watchList/`, body, {
      headers: new HttpHeaders().set(
        'Authorization',
        'Bearer' + ' ' + this.storagesrv.get('token')
      ),
    });
  }

  // Remove symbol from watchlist via API
  // DELETE /tools/dashboard/watchList/symbol with body: { "symbol": "AAPL" }
  removeSymbolFromWatchList(watchlistId: string, symbol: string) {
    const body = { symbol };
    return this.http.delete(`${baseUrl}/tools/dashboard/watchList/symbol`, {
      body,
      headers: new HttpHeaders().set(
        'Authorization',
        'Bearer' + ' ' + this.storagesrv.get('token')
      ),
    });
  }
  
    
}

